import { type FC, type ReactNode } from 'react';

import { TrippedStatusIcon } from '@/alerts/view/components/alert-status-badge';
import type { AlertStatus } from '@/types/alert-status';
import { cls } from '@/utils/cls';

export type AlertStatusBadgeProps = {
  /**
   * When `status` is null/undefined the badge falls back to a neutral state.
   * If `pending` is true, a pulsing dot + "Pending" label signals that
   * analytics are in-flight. Otherwise a quiet "—" is shown.
   */
  status?: AlertStatus | null;
  pending?: boolean;
};

/* -------------------------------------------------------------------------- */
/*  Mappings for active statuses (fed by BE)                                   */
/* -------------------------------------------------------------------------- */
const labelFromStatus: Record<AlertStatus, string> = {
  OK: 'OK',
  Info: 'Info',
  Warning: 'Warning',
  Tripped: 'Tripped',
  Critical: 'Critical',
};

const bgFromStatus: Record<AlertStatus, string> = {
  OK: 'bg-emerald-500/10',
  Info: 'bg-[#86DFC2]/30',
  Warning: 'bg-yellow-500/30',
  Tripped: 'bg-[#A97B50]/30',
  Critical: 'bg-red-500/30',
};

const colorFromStatus: Record<AlertStatus, string> = {
  OK: 'bg-emerald-500',
  Info: 'bg-[#86DFC2]',
  Warning: 'bg-yellow-500',
  Tripped: 'bg-[#A97B50]',
  Critical: 'bg-red-500',
};

/* -------------------------------------------------------------------------- */
/*  Neutral fallback colours – used when status is undefined (Inactive)        */
/* -------------------------------------------------------------------------- */
const neutralBg = 'bg-gray-300/30';
const neutralDot = 'bg-gray-400';

interface TooltipProps {
  content: string;
  children: ReactNode;
}

export const Tooltip: FC<TooltipProps> = ({ content, children }) => (
  <div className="group relative inline-block">
    {children}
    <div className="pointer-events-none absolute bottom-full left-1/2 mb-2 -translate-x-1/2 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
      <div className="rounded bg-gray-800 px-2 py-1 text-xs text-white shadow-lg">
        {content}
      </div>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
export const StatusLabel: FC<AlertStatusBadgeProps> = ({ status, pending }) => (
  <p className="text-xs leading-5 text-gray-500">
    {status ? labelFromStatus[status] : pending ? 'Pending' : '—'}
  </p>
);

export const StatusIcon: FC<AlertStatusBadgeProps> = ({ status, pending }) => {
  const tooltipText = status
    ? labelFromStatus[status]
    : pending
      ? 'Pending'
      : '—';
  const bg = status ? bgFromStatus[status] : neutralBg;
  const dot = status ? colorFromStatus[status] : neutralDot;

  if (status === 'Tripped') {
    return <TrippedStatusIcon />;
  }

  return (
    <Tooltip content={tooltipText}>
      <div className={cls('rounded-full p-1', bg)}>
        <div
          className={cls(
            'size-1.5 rounded-full',
            dot,
            pending && 'animate-pulse',
          )}
        />
      </div>
    </Tooltip>
  );
};

export const AlertStatusBadge: FC<AlertStatusBadgeProps> = ({
  status,
  pending,
}) => (
  <div className="mt-1 flex items-center gap-x-1.5">
    <StatusLabel status={status} pending={pending} />
    <StatusIcon status={status} pending={pending} />
  </div>
);
